import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from "flarum/forum/app";
import FormModal from "flarum/common/components/FormModal";
import Alert from "flarum/common/components/Alert";
import Button from "flarum/common/components/Button";
import LoadingIndicator from "flarum/common/components/LoadingIndicator";
import Switch from "flarum/common/components/Switch";
import Stream from "flarum/common/utils/Stream";
import classList from "flarum/common/utils/classList";
import withAttr from "flarum/common/utils/withAttr";
import chatState from "../state/chat";
import afterModalClosed from "../utils/afterModalClosed";
import EmojiPicker from "./EmojiPicker";
import { resolveEmoji } from "../utils/emoji";
import { humanDuration } from "../utils/duration";
import { forumMaxMessageLength } from "../utils/messageLimit";

/**
 * Message-length presets, and the bounds the API clamps a custom value to.
 *
 * Kept in step with ChannelResource's `maxMessageLength` field by hand: the
 * server is the authority and clamps regardless, these exist so the form does
 * not offer a number the save would silently change.
 */
const LENGTH_STEPS = [500, 1000, 2000, 3000, 4000, 8000, 16000];
const MIN_LENGTH = 100;
const MAX_LENGTH = 50000;

/** Sentinel for the "custom" option — never a value that gets saved. */
const CUSTOM_LENGTH = "custom";
/**
 * Creates or edits a category channel.
 *
 * One component for both because the field set is identical — Discourse shows the
 * same form for "New channel" and the channel's settings tab. Only the title, the
 * submit label and whether the record already exists differ.
 *
 * Extends FormModal, not Modal: `Modal.wrapper()` returns a bare fragment, so a
 * `type="submit"` button inside it has no form and onsubmit never fires.
 */
export default class ChannelFormModal extends FormModal {
  constructor() {
    super(...arguments);
    _defineProperty(this, "name", void 0);
    _defineProperty(this, "description", void 0);
    _defineProperty(this, "emoji", void 0);
    _defineProperty(this, "uploadingImage", false);
    /**
     * Which immediate action is running, or null.
     *
     * Distinct from `loading`, and both are needed. `loading` is core's form-wide
     * gate — it answers "is anything in flight", which is what every field and
     * every button reads to disable itself, and that part was right.
     *
     * What it cannot answer is *which* action is running, and a spinner is a claim
     * about one control rather than about the form. Driving four buttons from the
     * one flag meant pressing Save spun Close, Archive and Delete along with it,
     * as though the dialog had started four things at once.
     */
    _defineProperty(this, "pending", null);
    /**
     * A picture chosen on the create form, held until the channel exists.
     *
     * The upload route is addressed to a channel id, so on create there is nothing
     * to send it to yet. Keeping the file here and attaching it right after the
     * save is what lets the picture be part of creating a channel rather than a
     * second trip through the settings modal.
     */
    _defineProperty(this, "pendingImage", null);
    /**
     * `URL.createObjectURL` handle for the preview of {@link pendingImage}.
     *
     * Tracked separately because it has to be revoked — the browser keeps the blob
     * alive for the lifetime of the document otherwise, and picking three pictures
     * before saving would leak all three.
     */
    _defineProperty(this, "pendingImageUrl", null);
    /**
     * Which of the two icon fields is in use.
     *
     * A channel has one mark, not two: `channelIcon` already resolves picture over
     * emoji, so a channel carrying both silently ignores one of them and the form
     * showed no sign of which. The switch makes that choice the thing being edited,
     * and {@link onsubmit} enforces it in the data — the losing field is cleared
     * rather than left behind to reappear if the winner is ever removed.
     */
    _defineProperty(this, "useImage", void 0);
    _defineProperty(this, "tagId", void 0);
    _defineProperty(this, "threading", void 0);
    _defineProperty(this, "slowMode", void 0);
    /** A string, like `slowMode`: it backs a <select>. "" means "follow the forum". */
    _defineProperty(this, "maxMessageLength", void 0);
    /** Whether the custom number field is showing instead of a preset. */
    _defineProperty(this, "customLength", void 0);
    _defineProperty(this, "autoJoin", void 0);
    _defineProperty(this, "allowChannelWide", void 0);
    _defineProperty(this, "autoJoinOnReply", void 0);
    _defineProperty(this, "isPrivate", void 0);
    _defineProperty(this, "postPermission", void 0);
    _defineProperty(this, "postDiscussions", void 0);
  }
  oninit(vnode) {
    var _channel$name, _channel$description, _channel$emoji, _channel$slowModeSeco, _channel$maxMessageLe, _channel$postPermissi;
    super.oninit(vnode);
    const channel = this.attrs.channel;
    this.name = Stream((_channel$name = channel == null ? void 0 : channel.name()) != null ? _channel$name : "");
    this.description = Stream((_channel$description = channel == null ? void 0 : channel.description()) != null ? _channel$description : "");
    this.emoji = Stream((_channel$emoji = channel == null ? void 0 : channel.emoji()) != null ? _channel$emoji : "");

    // Whichever the channel already uses. A saved picture is the one that shows,
    // so opening the form on the emoji field would misreport the channel.
    this.useImage = Stream(Boolean(channel == null ? void 0 : channel.imageUrl()));
    this.tagId = Stream(channel != null && channel.tagId() ? String(channel.tagId()) : "");

    // A string, because it backs a <select> whose values are strings. Coerced
    // on submit rather than here, so an unchanged form round-trips exactly.
    this.slowMode = Stream(String((_channel$slowModeSeco = channel == null ? void 0 : channel.slowModeSeconds()) != null ? _channel$slowModeSeco : 0));

    // Empty string, not "0": the option that means "follow the forum" has to
    // round-trip as null, and 0 would read as a limit of zero characters.
    this.maxMessageLength = Stream(channel != null && channel.maxMessageLength() ? String(channel.maxMessageLength()) : "");

    // A channel already set to something off the list opens on the custom
    // field rather than snapping to the nearest preset.
    const saved = (_channel$maxMessageLe = channel == null ? void 0 : channel.maxMessageLength()) != null ? _channel$maxMessageLe : 0;
    this.customLength = Stream(saved > 0 && !LENGTH_STEPS.includes(saved));
    this.threading = Stream(channel ? Boolean(channel.threadingEnabled()) : Boolean(app.forum.attribute("ramon-chat.threadingDefault")));
    this.autoJoin = Stream(channel ? Boolean(channel.autoJoin()) : false);
    this.allowChannelWide = Stream(channel ? channel.allowChannelWideMentions() !== false : true);
    this.autoJoinOnReply = Stream(channel ? Boolean(channel.autoJoinOnReply()) : false);

    // Public by default: a channel nobody can find is the surprising outcome, so
    // it has to be chosen rather than fallen into.
    this.isPrivate = Stream(channel ? Boolean(channel.isPrivate()) : false);
    this.postPermission = Stream((_channel$postPermissi = channel == null ? void 0 : channel.postPermission()) != null ? _channel$postPermissi : "all");
    this.postDiscussions = Stream(channel ? Boolean(channel.postDiscussions()) : false);
  }

  /**
   * Releases the staged picture's object URL.
   *
   * Closing the modal without saving is the common path — the file was never
   * uploaded, so nothing on the server needs cleaning up, but the blob handle
   * would outlive the component.
   */
  onremove(vnode) {
    super.onremove(vnode);
    this.discardPendingImage();
  }
  isEditing() {
    return Boolean(this.attrs.channel);
  }
  className() {
    return "ChatModal ChannelFormModal Modal--large";
  }
  title() {
    return app.translator.trans(this.isEditing() ? "ramon-chat.forum.edit_channel.title" : "ramon-chat.forum.browse.new_channel");
  }

  /**
   * Replaces core's centred header + body pair with a header / body / footer
   * dialog.
   *
   * The form was a single 375px column of eighteen stacked fields, which is the
   * shape core's `inner()` produces and the reason it read as a questionnaire:
   * the one required field, the name, sat at the same weight and in the same
   * rhythm as an admin-only toggle six scrolls below it. Everything else here —
   * the two columns, the grouping, the pinned submit — depends on owning this
   * wrapper, so it is overridden rather than worked around from `content()`.
   */
  inner() {
    return m('[', null, this.header(), !!this.alertAttrs && m("div", {
      className: "Modal-alert"
    }, m(Alert, this.alertAttrs)), this.content(), this.footer());
  }

  /**
   * Icon, title and one line of orientation.
   *
   * The icon mirrors the emoji or picture currently chosen, so the field that is
   * furthest from the header still shows its effect without scrolling back.
   */
  header() {
    // Follows the switch rather than the stored values: while the form is open
    // the icon shows what saving would produce, which is the whole point of a
    // preview. A channel with a picture whose owner has just chosen an emoji
    // must preview the emoji.
    const image = this.useImage() ? this.iconImageUrl() : null;
    const emoji = this.useImage() ? null : resolveEmoji(this.emoji());
    const name = this.name().trim();
    return m("div", {
      className: "Modal-header ChannelFormModal-header"
    }, m("div", {
      className: "ChannelFormModal-headerIcon",
      "aria-hidden": "true"
    }, image ? m("img", {
      src: image,
      alt: ""
    }) : emoji ? m("span", null, emoji) : m("i", {
      className: "fas fa-hashtag"
    })), m("div", {
      className: "ChannelFormModal-headerText"
    }, m("h3", {
      className: "App-titleControl App-titleControl--text"
    }, this.title()), m("p", {
      className: "ChannelFormModal-headerSubtitle"
    }, name || app.translator.trans(this.isEditing() ? "ramon-chat.forum.edit_channel.subtitle" : "ramon-chat.forum.new_channel.subtitle"))));
  }
  content() {
    return m("div", {
      className: "Modal-body ChannelFormModal-body"
    }, m("div", {
      className: "Form ChannelFormModal-grid"
    }, this.identitySection(), this.accessSection(), this.behaviourSection(), this.lifecycle()));
  }

  /** Name, description and the channel's icon. */
  identitySection() {
    return this.section("ramon-chat.forum.new_channel.section_identity", [
    // Icon and name on one line: the emoji is a property of the name, not a
    // field of its own, and stacking them put an empty picker row between
    // the two things the reader is actually comparing. This works because
    // the section spans both columns — in one track the picker's search
    // field would have about 90px to live in.
    m("div", {
      className: "Form-group ChannelFormModal-nameRow"
    }, m("div", null, m("label", null, app.translator.trans("ramon-chat.forum.new_channel.name")), m("input", {
      className: "FormControl",
      type: "text",
      maxlength: 100,
      bidi: this.name,
      placeholder: app.translator.trans("ramon-chat.forum.new_channel.name_placeholder", {}, true),
      disabled: this.loading
    })), this.iconField()), m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans("ramon-chat.forum.new_channel.description")), m("textarea", {
      className: "FormControl",
      rows: 2,
      maxlength: 1000,
      bidi: this.description,
      disabled: this.loading
    }))], "ChannelFormModal-section--wide");
  }

  /**
   * The channel's mark: an emoji, or a picture instead of one.
   *
   * One slot holding whichever the switch selects, rather than both fields on
   * screen at once. Two always-visible fields for a value that can only be one
   * of them invites setting both and then wondering why only the picture shows.
   */
  iconField() {
    const picture = this.useImage();
    return m("div", {
      className: "ChannelFormModal-iconField"
    }, m("div", {
      className: "ChannelFormModal-iconField-head"
    }, m("label", null, app.translator.trans(picture ? "ramon-chat.forum.new_channel.image" : "ramon-chat.forum.new_channel.emoji")), m("button", {
      type: "button",
      className: "ChannelFormModal-iconKind",
      role: "switch",
      "aria-checked": picture ? "true" : "false",
      "aria-label": app.translator.trans("ramon-chat.forum.new_channel.icon_kind", {}, true),
      title: app.translator.trans(picture ? "ramon-chat.forum.new_channel.icon_kind_to_emoji" : "ramon-chat.forum.new_channel.icon_kind_to_image", {}, true),
      disabled: this.loading || this.uploadingImage,
      onclick: () => this.chooseIconKind(!picture)
    }, this.iconKindSide(false, "far fa-face-smile"), this.iconKindSide(true, "fas fa-image"))), m("div", {
      className: "ChannelFormModal-iconField-body"
    }, picture ? this.image() : m('[', null, m(EmojiPicker, {
      value: this.emoji(),
      onchange: value => this.emoji(value != null ? value : ""),
      disabled: this.loading
    }), m("div", {
      className: "helpText"
    }, app.translator.trans("ramon-chat.forum.new_channel.emoji_help")))));
  }

  /**
   * One half of the switch. A span, not a button: the whole control is the
   * button, so a click on either side flips it.
   */
  iconKindSide(picture, icon) {
    return m("span", {
      className: classList("ChannelFormModal-iconKind-option", {
        "ChannelFormModal-iconKind-option--active": this.useImage() === picture
      }),
      "aria-hidden": "true"
    }, m("i", {
      className: icon
    }));
  }

  /**
   * Switches between the two icon fields.
   *
   * Turning the picture off drops a file staged for a channel that does not
   * exist yet: it is never going to be uploaded now, and holding it would
   * attach it anyway if the switch were flipped back on after the save. A
   * picture already on the server is left alone until the form is submitted, so
   * the change stays cancellable — see {@link onsubmit}.
   */
  chooseIconKind(picture) {
    this.useImage(picture);
    if (!picture) this.discardPendingImage();
  }

  /** Who can see the channel, who can write in it, and what it inherits. */
  accessSection() {
    return this.section("ramon-chat.forum.new_channel.section_access", [this.visibility(), this.posting(), this.tagOptions()]);
  }

  /** How the channel behaves once it exists. */
  behaviourSection() {
    var _app$session$user;
    const tag = this.selectedTag();
    return this.section("ramon-chat.forum.new_channel.section_behaviour", [this.slowModeOptions(), this.messageLengthOptions(), m("div", {
      className: "Form-group ChannelFormModal-toggles"
    }, this.toggle(this.threading, "ramon-chat.forum.info.threading", app.translator.trans("ramon-chat.forum.new_channel.threading_help")), this.toggle(this.allowChannelWide, "ramon-chat.forum.settings.channel_wide_mentions", app.translator.trans("ramon-chat.forum.new_channel.channel_wide_mentions_help")), this.toggle(this.autoJoinOnReply, "ramon-chat.forum.info.auto_join_on_reply", tag ? app.translator.trans("ramon-chat.forum.info.auto_join_on_reply_help_bound", {
      category: tag.name()
    }) : app.translator.trans("ramon-chat.forum.info.auto_join_on_reply_help_none")), tag ? this.toggle(this.postDiscussions, "ramon-chat.forum.info.post_discussions", app.translator.trans("ramon-chat.forum.info.post_discussions_help_bound", {
      category: tag.name()
    })) : null, ((_app$session$user = app.session.user) == null ? void 0 : _app$session$user.attribute("isAdmin")) !== false ? this.toggle(this.autoJoin, "ramon-chat.forum.info.auto_join", app.translator.trans("ramon-chat.forum.info.auto_join_help")) : null)]);
  }

  /**
   * A titled group of fields, dropped entirely when it has nothing to show —
   * an empty bordered box with a heading is worse than no box.
   */
  section(titleKey, children, className) {
    const content = children.filter(Boolean);
    if (content.length === 0) return null;
    return m("section", {
      className: classList("ChannelFormModal-section", className)
    }, m("h4", {
      className: "ChannelFormModal-sectionTitle"
    }, app.translator.trans(titleKey)), content);
  }

  /**
   * One switch row: control, label and the sentence explaining what it changes.
   *
   * A `Switch` rather than a bare checkbox because these are settings that take
   * effect on save, not items being ticked off a list — and because the help
   * text needs to sit under the label, which a checkbox's inline layout cannot
   * do without a hardcoded indent matching the box's width.
   */
  toggle(stream, labelKey,
  // Rendered children rather than a translator key: two of these rows change
  // their sentence with the category picked above, and a key alone cannot
  // carry the interpolated category name.
  help) {
    return m("div", {
      className: "ChannelFormModal-toggle"
    }, m(Switch, {
      state: stream(),
      onchange: checked => stream(checked),
      disabled: this.loading
    }, app.translator.trans(labelKey)), help ? m("div", {
      className: "helpText"
    }, help) : null);
  }

  /**
   * The submit row, pinned below the scrolling body.
   *
   * It used to be the last of eighteen stacked fields, which meant that on a
   * short viewport the only way to find out the form could be submitted was to
   * scroll past every optional setting.
   */
  footer() {
    return m("div", {
      className: "Modal-footer ChannelFormModal-footer"
    }, m(Button, {
      className: "Button Button--link",
      type: "button",
      disabled: this.loading,
      onclick: () => this.hide()
    }, app.translator.trans("ramon-chat.forum.new_channel.cancel")), m(Button, {
      className: "Button Button--primary",
      type: "submit",
      loading: this.loading && this.pending === null,
      disabled: this.loading || this.name().trim() === ""
    }, app.translator.trans(this.isEditing() ? "ramon-chat.forum.edit_channel.submit" : "ramon-chat.forum.new_channel.submit")));
  }

  /**
   * A picture for the channel, instead of its emoji.
   *
   * Offered on both forms, but the two behave differently underneath. Editing
   * uploads immediately, because the channel id the route needs already exists.
   * Creating cannot: the id is only minted by the save. There the file is held
   * in {@link pendingImage} and attached once the record comes back — see
   * {@link attachPendingImage}.
   *
   * The alternative, hiding the field until the channel exists, is what this
   * replaced: it read as "channels cannot have pictures" and sent people back
   * through the settings modal for something they had already decided.
   *
   * No label of its own: it fills the icon slot, which {@link iconField} has
   * already labelled.
   */
  image() {
    const url = this.iconImageUrl();
    return m("div", {
      className: "ChatChannelForm-image"
    }, m("div", {
      className: "ChatChannelForm-imageRow"
    }, m("div", {
      className: "ChatChannelForm-imagePreview"
    }, url ? m("img", {
      src: url,
      alt: ""
    }) : m("i", {
      className: "fas fa-hashtag",
      "aria-hidden": "true"
    })), this.uploadingImage ? m(LoadingIndicator, {
      display: "inline",
      size: "small"
    }) : m('[', null, m("label", {
      className: "Button"
    }, app.translator.trans("ramon-chat.forum.new_channel.image_upload"), m("input", {
      type: "file",
      accept: "image/*",
      style: {
        display: "none"
      },
      onchange: e => this.chooseImage(e)
    })), url ? m(Button, {
      className: "Button",
      onclick: () => this.removeImage()
    }, app.translator.trans("ramon-chat.forum.new_channel.image_remove")) : null)), m("div", {
      className: "helpText"
    }, app.translator.trans("ramon-chat.forum.new_channel.image_help")));
  }

  /** The picture standing in for the channel: staged on create, saved on edit. */
  iconImageUrl() {
    return this.attrs.channel ? this.attrs.channel.imageUrl() : this.pendingImageUrl;
  }

  /**
   * Routes the picked file: straight to the server when the channel exists,
   * into {@link pendingImage} when it does not.
   */
  chooseImage(e) {
    var _input$files;
    if (this.isEditing()) {
      void this.uploadImage(e);
      return;
    }
    const input = e.target;
    const file = (_input$files = input.files) == null ? void 0 : _input$files[0];

    // Cleared so re-picking the same file still fires a change event.
    input.value = "";
    if (!file) return;
    this.discardPendingImage();
    this.pendingImage = file;
    this.pendingImageUrl = URL.createObjectURL(file);
    m.redraw();
  }

  /** Drops the picture: a DELETE when saved, a local discard when staged. */
  removeImage() {
    if (this.isEditing()) {
      void this.clearImage();
      return;
    }
    this.discardPendingImage();
    m.redraw();
  }
  discardPendingImage() {
    if (this.pendingImageUrl) URL.revokeObjectURL(this.pendingImageUrl);
    this.pendingImage = null;
    this.pendingImageUrl = null;
  }

  /**
   * Sends the staged picture to the channel that was just created.
   *
   * A failure here does NOT undo the channel — it exists, it is in the sidebar,
   * and the only thing missing is the picture. Saying so and letting the user
   * add it from the settings modal is honest; rolling back a channel they asked
   * for because a JPEG was too large would not be.
   */
  async attachPendingImage(channel) {
    const file = this.pendingImage;
    if (!file) return;
    const body = new FormData();
    body.append("image", file);
    try {
      var _response$data$imageU, _response$data;
      const response = await app.request({
        method: "POST",
        url: "".concat(app.forum.attribute("apiUrl"), "/chat/channels/").concat(channel.id(), "/image"),
        serialize: raw => raw,
        body
      });
      channel.pushAttributes({
        imageUrl: (_response$data$imageU = response == null || (_response$data = response.data) == null ? void 0 : _response$data.imageUrl) != null ? _response$data$imageU : null
      });
    } catch (err) {
      var _err$response$errors$, _err$response;
      app.alerts.show({
        type: "error"
      }, (_err$response$errors$ = err == null || (_err$response = err.response) == null || (_err$response = _err$response.errors) == null || (_err$response = _err$response[0]) == null ? void 0 : _err$response.detail) != null ? _err$response$errors$ : app.translator.trans("ramon-chat.forum.new_channel.image_failed_after_create"));
    } finally {
      this.discardPendingImage();
    }
  }
  async uploadImage(e) {
    var _input$files2;
    const input = e.target;
    const file = (_input$files2 = input.files) == null ? void 0 : _input$files2[0];
    const channel = this.attrs.channel;
    if (!file || !channel) return;
    this.uploadingImage = true;
    m.redraw();
    const body = new FormData();
    body.append("image", file);
    try {
      var _response$data$imageU2, _response$data2;
      const response = await app.request({
        method: "POST",
        url: "".concat(app.forum.attribute("apiUrl"), "/chat/channels/").concat(channel.id(), "/image"),
        // The default serializer would JSON-encode the FormData into "[object
        // FormData]" and the request would arrive with no file at all.
        serialize: raw => raw,
        body
      });
      channel.pushAttributes({
        imageUrl: (_response$data$imageU2 = response == null || (_response$data2 = response.data) == null ? void 0 : _response$data2.imageUrl) != null ? _response$data$imageU2 : null
      });
    } catch (err) {
      var _err$response$errors$2, _err$response2;
      app.alerts.show({
        type: "error"
      }, (_err$response$errors$2 = err == null || (_err$response2 = err.response) == null || (_err$response2 = _err$response2.errors) == null || (_err$response2 = _err$response2[0]) == null ? void 0 : _err$response2.detail) != null ? _err$response$errors$2 : app.translator.trans("ramon-chat.forum.new_channel.image_failed"));
    } finally {
      this.uploadingImage = false;
      // Cleared so re-picking the same file still fires a change event.
      input.value = "";
      m.redraw();
    }
  }
  async clearImage() {
    const channel = this.attrs.channel;
    if (!channel) return;
    this.uploadingImage = true;
    m.redraw();
    try {
      await app.request({
        method: "DELETE",
        url: "".concat(app.forum.attribute("apiUrl"), "/chat/channels/").concat(channel.id(), "/image")
      });
      channel.pushAttributes({
        imageUrl: null
      });
    } catch (_unused) {
      app.alerts.show({
        type: "error"
      }, app.translator.trans("ramon-chat.forum.new_channel.image_failed"));
    } finally {
      this.uploadingImage = false;
      m.redraw();
    }
  }
  visibility() {
    return m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans("ramon-chat.forum.new_channel.visibility")), m("div", {
      className: "ChannelFormModal-choices"
    }, this.choice(false, "fas fa-globe", "ramon-chat.forum.new_channel.public", "ramon-chat.forum.new_channel.public_help"), this.choice(true, "fas fa-lock", "ramon-chat.forum.new_channel.private", "ramon-chat.forum.new_channel.private_help")));
  }

  /** One visibility card. Still a radio underneath — keyboard and screen readers get the group semantics for free. */
  choice(value, icon, labelKey, helpKey) {
    const selected = this.isPrivate() === value;
    return m("label", {
      className: classList("ChannelFormModal-choice", {
        "ChannelFormModal-choice--selected": selected
      })
    }, m("input", {
      type: "radio",
      name: "ramon-chat-visibility",
      checked: selected,
      onchange: () => this.isPrivate(value),
      disabled: this.loading
    }), m("span", {
      className: "ChannelFormModal-choice-title"
    }, m("i", {
      className: icon,
      "aria-hidden": "true"
    }), app.translator.trans(labelKey)), m("span", {
      className: "ChannelFormModal-choice-help"
    }, app.translator.trans(helpKey)));
  }

  /**
   * Who may post.
   *
   * A separate question from visibility: "who can see this" and "who can write in
   * it" are independent, and a private channel only moderators post in is a
   * perfectly ordinary announcement channel for an invited audience.
   */
  posting() {
    return m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans("ramon-chat.forum.new_channel.post_permission")), m("select", {
      className: "FormControl",
      value: this.postPermission(),
      onchange: withAttr("value", this.postPermission),
      disabled: this.loading
    }, m("option", {
      value: "all"
    }, app.translator.trans("ramon-chat.forum.new_channel.post_all", {}, true)), m("option", {
      value: "moderators"
    }, app.translator.trans("ramon-chat.forum.new_channel.post_moderators", {}, true))), m("div", {
      className: "helpText"
    }, app.translator.trans(this.postPermission() === "moderators" ? "ramon-chat.forum.new_channel.post_moderators_help" : "ramon-chat.forum.new_channel.post_all_help")));
  }

  /**
   * Close and archive, on an existing channel only.
   *
   * Neither means anything for a channel that does not exist yet, and both act
   * immediately rather than on submit — they are not settings being edited, they
   * are state changes, and mixing them into the form's save would make an
   * unsaved-and-abandoned form able to archive something.
   */
  lifecycle() {
    const channel = this.attrs.channel;
    if (!channel) return null;
    const closed = channel.status() === "closed";
    const archived = Boolean(channel.archivedAt());
    const items = [];
    if (channel.canClose() && !archived) {
      items.push(m(Button, {
        className: "Button",
        icon: closed ? "fas fa-lock-open" : "fas fa-lock",
        loading: this.pending === "status",
        disabled: this.loading,
        onclick: () => this.setStatus(closed ? "open" : "closed")
      }, app.translator.trans(closed ? "ramon-chat.forum.info.reopen_channel" : "ramon-chat.forum.info.close_channel")));
    }
    if (channel.canArchive() && !archived) {
      items.push(m(Button, {
        className: "Button",
        icon: "fas fa-box-archive",
        loading: this.pending === "archive",
        disabled: this.loading,
        onclick: () => this.archive()
      }, app.translator.trans("ramon-chat.forum.info.archive_channel")));
    }

    // Deleting is offered from here as well as from the channel's info panel:
    // this dialog is where "the channel's settings" live, and an irreversible
    // action that exists only behind another surface reads as missing.
    const canDelete = channel.canDelete();
    if (items.length === 0 && !canDelete) return null;
    return m("div", {
      className: "ChannelFormModal-lifecycle ChannelFormModal-section ChannelFormModal-section--wide"
    }, m("label", {
      className: "ChannelFormModal-sectionTitle"
    }, app.translator.trans("ramon-chat.forum.edit_channel.lifecycle")), items.length > 0 ? m("div", {
      className: "ChannelFormModal-lifecycleActions"
    }, items) : null, m("div", {
      className: "helpText"
    }, app.translator.trans("ramon-chat.forum.edit_channel.lifecycle_help")), canDelete ? m("div", {
      className: "ChannelFormModal-lifecycleDanger"
    }, m(Button, {
      className: "Button ChannelFormModal-deleteButton",
      icon: "fas fa-trash",
      loading: this.pending === "delete",
      disabled: this.loading,
      onclick: () => this.destroy()
    }, app.translator.trans("ramon-chat.forum.info.delete_channel"))) : null);
  }

  /**
   * Deletes the channel and closes the dialog.
   *
   * Its own path rather than `act()`: that one posts to an endpoint and then
   * hands the saved channel to `onSaved`, and neither makes sense once the
   * record is gone. The callers that pass `onSaved` on the *edit* path use it to
   * refresh a list, which is exactly what should happen here too — the create
   * path cannot reach this code, because a channel that does not exist yet has
   * no `canDelete()` to be true.
   */
  async destroy() {
    const channel = this.attrs.channel;
    if (!channel) return;
    if (!confirm(app.translator.trans("ramon-chat.forum.info.delete_confirm", {}, true))) return;
    this.pending = "delete";
    this.loading = true;
    m.redraw();
    try {
      await channel.delete();
      chatState.channels = chatState.channels.filter(c => c.id() !== channel.id());

      // Leaving the active channel pointing at a deleted record strands the
      // stream on something that can no longer be fetched.
      if (chatState.activeChannelId === Number(channel.id())) {
        chatState.setActiveChannel(null);
      }
      this.hide();
      afterModalClosed(() => {
        var _this$attrs$onSaved, _this$attrs;
        return (_this$attrs$onSaved = (_this$attrs = this.attrs).onSaved) == null ? void 0 : _this$attrs$onSaved.call(_this$attrs, channel);
      });
    } catch (e) {
      var _e$response$errors$0$, _e$response;
      app.alerts.show({
        type: "error"
      }, (_e$response$errors$0$ = e == null || (_e$response = e.response) == null || (_e$response = _e$response.errors) == null || (_e$response = _e$response[0]) == null ? void 0 : _e$response.detail) != null ? _e$response$errors$0$ : app.translator.trans("ramon-chat.forum.edit_channel.failed"));
    } finally {
      this.pending = null;
      this.loading = false;
      m.redraw();
    }
  }
  async setStatus(status) {
    await this.act("status", "/chat-channels/".concat(this.attrs.channel.id(), "/status"), {
      status
    });
  }
  async archive() {
    if (!confirm(app.translator.trans("ramon-chat.forum.edit_channel.archive_confirm", {}, true))) return;
    await this.act("archive", "/chat-channels/".concat(this.attrs.channel.id(), "/archive"), {});
  }

  /**
   * Runs one immediate state change.
   *
   * `action` names the button that owns the spinner for the duration; `loading`
   * still gates the rest of the form, so nothing else can be started meanwhile.
   */
  async act(action, path, attributes) {
    this.pending = action;
    this.loading = true;
    m.redraw();
    try {
      const payload = await app.request({
        method: "POST",
        url: "".concat(app.forum.attribute("apiUrl")).concat(path),
        body: {
          data: {
            attributes
          }
        }
      });
      if (payload != null && payload.data) app.store.pushPayload(payload);
      this.hide();

      // Same reason as the save path above: whatever the caller does next must not
      // race the close animation.
      afterModalClosed(() => {
        var _this$attrs$onSaved2, _this$attrs2;
        return (_this$attrs$onSaved2 = (_this$attrs2 = this.attrs).onSaved) == null ? void 0 : _this$attrs$onSaved2.call(_this$attrs2, this.attrs.channel);
      });
    } catch (e) {
      var _e$response$errors$0$2, _e$response2;
      app.alerts.show({
        type: "error"
      }, (_e$response$errors$0$2 = e == null || (_e$response2 = e.response) == null || (_e$response2 = _e$response2.errors) == null || (_e$response2 = _e$response2[0]) == null ? void 0 : _e$response2.detail) != null ? _e$response$errors$0$2 : app.translator.trans("ramon-chat.forum.edit_channel.failed"));
    } finally {
      this.pending = null;
      this.loading = false;
      m.redraw();
    }
  }

  /**
   * Category picker, rendered only when flarum/tags is present. A tag-bound
   * channel inherits that tag's permissions, which is how a restricted category
   * yields a restricted channel.
   */
  tagOptions() {
    var _flarum$extensions;
    const tags = app.store.all("tags");
    if (!("flarum-tags" in ((_flarum$extensions = flarum.extensions) != null ? _flarum$extensions : {})) || tags.length === 0) return null;
    const selected = this.selectedTag();
    return m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans("ramon-chat.forum.new_channel.category")), m("select", {
      className: "FormControl",
      value: this.tagId(),
      onchange: withAttr("value", id => this.chooseTag(id)),
      disabled: this.loading
    }, m("option", {
      value: ""
    }, app.translator.trans("ramon-chat.forum.new_channel.no_category")), tags.filter(tag => {
      var _tag$isChild;
      return !((_tag$isChild = tag.isChild) != null && _tag$isChild.call(tag));
    }).map(tag => m("option", {
      key: tag.id(),
      value: String(tag.id())
    }, tag.name()))), m("div", {
      className: "helpText"
    }, selected ? app.translator.trans("ramon-chat.forum.new_channel.category_help_bound", {
      category: selected.name()
    }) : app.translator.trans("ramon-chat.forum.new_channel.category_help_none")));
  }

  /**
   * Picks the bound category, or clears it.
   *
   * Clearing also switches off "announce new discussions": that toggle is only
   * rendered while a category is chosen, so leaving it set would submit a value
   * the reader can no longer see or reach — and the channel would start
   * announcing the moment someone bound a category to it later.
   */
  chooseTag(id) {
    this.tagId(id);
    if (!id) this.postDiscussions(false);
  }

  /** The category currently chosen in the picker, if any. */
  selectedTag() {
    var _app$store$all$find;
    if (!this.tagId()) return null;
    return (_app$store$all$find = app.store.all("tags").find(tag => String(tag.id()) === this.tagId())) != null ? _app$store$all$find : null;
  }

  /**
   * Nothing awaits this method, so a rejection here would surface as an unhandled
   * promise rejection rather than as feedback. Every failure path is handled inline.
   */
  /**
   * Slow mode: how long each person waits between messages here.
   *
   * A fixed list rather than a free number field. The useful values are few and
   * far apart — five seconds calms a room, five minutes changes what the room is
   * for — and a text box invites 7s, which is nobody's intention.
   *
   * Moderators are exempt, and the help text says so: someone enabling this needs
   * to know it will not throttle them out of their own moderation. That sentence
   * only appears once a wait is actually set — with slow mode off there is no
   * exemption to explain, and describing one implies a limit that is not there.
   */
  slowModeOptions() {
    const steps = [0, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 21600];
    const seconds = Number(this.slowMode()) || 0;
    return m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans("ramon-chat.forum.new_channel.slow_mode")), m("select", {
      className: "FormControl",
      value: this.slowMode(),
      onchange: withAttr("value", this.slowMode),
      disabled: this.loading
    }, steps.map(seconds => m("option", {
      key: seconds,
      value: String(seconds)
    }, seconds === 0 ? app.translator.trans("ramon-chat.forum.new_channel.slow_mode_off", {}, true) : humanDuration(seconds)))), m("p", {
      className: "helpText"
    }, seconds === 0 ? app.translator.trans("ramon-chat.forum.new_channel.slow_mode_help_off") : app.translator.trans("ramon-chat.forum.new_channel.slow_mode_help_on", {
      duration: humanDuration(seconds)
    })));
  }

  /**
   * How long a message may be here.
   *
   * Presets plus a way out of them. The list covers what most channels want and
   * keeps the common case to one click; "Custom" opens a number field for the
   * rooms that need something the list does not have. The first option is not a
   * number at all — it keeps the channel following the forum-wide setting, so
   * raising that later still raises this channel with it.
   */
  messageLengthOptions() {
    const forumDefault = forumMaxMessageLength();
    const value = this.maxMessageLength();
    const custom = this.customLength();
    return m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans("ramon-chat.forum.new_channel.message_length")), m("select", {
      className: "FormControl",
      value: custom ? CUSTOM_LENGTH : value,
      onchange: withAttr("value", picked => this.chooseMessageLength(picked)),
      disabled: this.loading
    }, m("option", {
      value: ""
    }, app.translator.trans("ramon-chat.forum.new_channel.message_length_inherit", {
      count: forumDefault
    }, true)), LENGTH_STEPS.map(chars => m("option", {
      key: chars,
      value: String(chars)
    }, app.translator.trans("ramon-chat.forum.new_channel.message_length_chars", {
      count: chars
    }, true))), m("option", {
      value: CUSTOM_LENGTH
    }, app.translator.trans("ramon-chat.forum.new_channel.message_length_custom", {}, true))), custom ? m("input", {
      className: "FormControl ChannelFormModal-customLength",
      type: "number",
      min: MIN_LENGTH,
      max: MAX_LENGTH,
      step: 100
      // Not `bidi`: the value has to survive being briefly empty while it
      // is retyped, and clamping on every keystroke would fight the
      // typist — "5" becomes "100" before the "00" arrives. It is clamped
      // on submit instead, where the server clamps too.
      ,
      value: value,
      placeholder: String(forumDefault),
      disabled: this.loading,
      oninput: withAttr("value", this.maxMessageLength),
      oncreate: vnode => vnode.dom.focus()
    }) : null, m("p", {
      className: "helpText"
    }, this.messageLengthHelp(forumDefault)));
  }

  /** The sentence under the field, describing the option actually selected. */
  messageLengthHelp(forumDefault) {
    const raw = this.maxMessageLength().trim();
    if (raw === "") {
      return app.translator.trans(this.customLength() ? "ramon-chat.forum.new_channel.message_length_help_empty" : "ramon-chat.forum.new_channel.message_length_help_inherit", {
        count: forumDefault
      });
    }
    const chars = Number(raw);

    // Says what saving would actually store, not what was typed: the server
    // clamps, and a help text promising 40 characters under a field the API
    // will raise to 100 is a lie the reader only discovers afterwards.
    if (chars < MIN_LENGTH || chars > MAX_LENGTH) {
      var _this$clampedMessageL;
      return app.translator.trans("ramon-chat.forum.new_channel.message_length_help_clamped", {
        count: (_this$clampedMessageL = this.clampedMessageLength()) != null ? _this$clampedMessageL : forumDefault
      });
    }
    return app.translator.trans("ramon-chat.forum.new_channel.message_length_help_own", {
      count: chars
    });
  }

  /** Switches between a preset, the forum default, and the custom field. */
  chooseMessageLength(picked) {
    if (picked === CUSTOM_LENGTH) {
      this.customLength(true);

      // Seeded with the preset that was showing, so the field opens on a
      // sensible number instead of empty.
      if (this.maxMessageLength() === "") {
        this.maxMessageLength(String(forumMaxMessageLength()));
      }
      return;
    }
    this.customLength(false);
    this.maxMessageLength(picked);
  }

  /**
   * The custom value as it will be stored: clamped to the range the API
   * accepts, or null when the field is empty or not a number.
   */
  clampedMessageLength() {
    const chars = Number(this.maxMessageLength().trim());
    if (!Number.isFinite(chars) || chars <= 0) return null;
    return Math.max(MIN_LENGTH, Math.min(MAX_LENGTH, Math.round(chars)));
  }
  onsubmit(e) {
    var _this$attrs$channel;
    e.preventDefault();
    if (this.loading) return;
    this.loading = true;
    const editing = this.isEditing();
    const attributes = {
      name: this.name().trim(),
      description: this.description().trim() || null,
      // Null when the channel is using a picture. The emoji stream keeps its
      // value so flipping the switch back restores the picker, but a channel
      // that stores both would show the picture and silently carry an emoji
      // that reappears the day the picture is deleted.
      emoji: this.useImage() ? null : this.emoji().trim() || null,
      threadingEnabled: this.threading(),
      slowModeSeconds: Number(this.slowMode()) || 0,
      maxMessageLength: this.clampedMessageLength(),
      allowChannelWideMentions: this.allowChannelWide(),
      autoJoin: this.autoJoin(),
      autoJoinOnReply: this.autoJoinOnReply(),
      isPrivate: this.isPrivate(),
      postPermission: this.postPermission(),
      postDiscussions: this.postDiscussions(),
      tagId: this.tagId() ? Number(this.tagId()) : null
    };

    // `type` is writable only on create; sending it on update would be rejected.
    if (!editing) {
      attributes.type = "category";
    }
    const record = (_this$attrs$channel = this.attrs.channel) != null ? _this$attrs$channel : app.store.createRecord("chat-channels");
    record.save(attributes).then(async channel => {
      // Before the sidebar and `onSaved` see it: both read `imageUrl()`, and a
      // channel that flashes a hash for a second and then swaps to the picture
      // looks like a bug in a list the user is already scanning.
      if (!editing && this.useImage() && this.pendingImage) {
        await this.attachPendingImage(channel);
      }

      // The other half of the exclusivity: the emoji was written by the save
      // above, so the picture it replaces has to go. Deferred to here rather
      // than done when the switch was flipped, so abandoning the form leaves
      // the channel exactly as it was found.
      if (editing && !this.useImage() && channel.imageUrl()) {
        await this.clearImage();
      }
      if (!editing && !chatState.channels.some(c => c.id() === channel.id())) {
        // The server subscribes the creator, so the channel belongs in the
        // sidebar straight away rather than after the next poll.
        chatState.channels.unshift(channel);
      }
      this.hide();
      if (editing) {
        app.alerts.show({
          type: "success"
        }, app.translator.trans("ramon-chat.forum.edit_channel.saved"));
      }

      // Deferred until the modal has actually gone.
      //
      // Core's ModalManager.animateHide() only clears its `modalClosing` latch
      // from a `transitionend` handler. A caller that changes the route in this
      // same tick can interrupt that transition, the event never fires, and the
      // latch stays set — after which every later animateHide() returns
      // immediately and no modal can be closed again until the page is reloaded.
      // That is why creating a channel left the next modal stuck with a dead X.
      afterModalClosed(() => {
        var _this$attrs$onSaved3, _this$attrs3;
        return (_this$attrs$onSaved3 = (_this$attrs3 = this.attrs).onSaved) == null ? void 0 : _this$attrs$onSaved3.call(_this$attrs3, channel);
      });
    }).catch(error => {
      this.loading = false;

      // FormModal.onerror renders validation errors above the form and refocuses
      // the offending field; anything else gets an alert so it is never silent.
      if (error != null && error.alert) {
        this.onerror(error);
      } else {
        var _error$response$error, _error$response;
        app.alerts.show({
          type: "error"
        }, (_error$response$error = error == null || (_error$response = error.response) == null || (_error$response = _error$response.errors) == null || (_error$response = _error$response[0]) == null ? void 0 : _error$response.detail) != null ? _error$response$error : app.translator.trans(editing ? "ramon-chat.forum.edit_channel.failed" : "ramon-chat.forum.new_channel.failed"));
        m.redraw();
      }
    });
  }
}
flarum.reg.add('ramon-chat', 'forum/components/ChannelFormModal', ChannelFormModal);